import { Fragment, useEffect, useRef, useState } from "react";
import { Transition } from "@headlessui/react";
import { createPortal } from "react-dom";

export default function DropdownLayout({
  activator,
  children,
  fullWidth = true,
  positionClassName = "right-0 origin-top-right",
  bodyClassName = "mt-2",
  open = false,
  autoClose = true,
  usePortal = false,
  portalZIndex = 9999,
  closeOnContentClick = false,
}) {
  const fullWClassName = fullWidth ? "w-full" : "";

  const dropDownRef = useRef(null);

  const [show, setShow] = useState(open);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  useEffect(() => {
    setShow(open);
  }, [open]);

  const handleClickOut = (event) => {
    if (autoClose) {
      const { target } = event;
      if (!dropDownRef || !dropDownRef.current) return;
      if (!dropDownRef.current.contains(target)) {
        setShow(false);
      }
    }
  };

  useEffect(() => {
    if (autoClose) {
      document.addEventListener("mousedown", handleClickOut);
      return () => {
        document.removeEventListener("mousedown", handleClickOut);
      };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setShow(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!usePortal) return;
    if (!show) return;
    if (!dropDownRef.current) return;
    if (typeof window === "undefined") return;

    const rect = dropDownRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }, [show, usePortal]);

  const dropdownContent = (
    <Transition
      as={Fragment}
      show={show}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <div
        className={`${
          usePortal ? "" : "absolute z-20"
        } ${bodyClassName} bg-white shadow-md ring-opacity-5 focus:outline-hidden ${fullWClassName} ${positionClassName}`}
        style={
          usePortal
            ? {
                position: "absolute",
                top: position.top,
                left: position.left,
                width: fullWidth ? position.width : undefined,
                zIndex: portalZIndex,
              }
            : undefined
        }
        onMouseDown={(e) => {
          // Keep dropdown open when interacting inside the body
          e.stopPropagation();
        }}
        onClick={() => {
          if (closeOnContentClick) {
            setShow(false);
          }
        }}
      >
        {children}
      </div>
    </Transition>
  );

  return (
    <div className={`relative rounded text-left ${fullWClassName}`} ref={dropDownRef}>
      <div
        className={`flex items-center  ${fullWClassName}`}
        onMouseDown={(e) => {
          // Prevent outer mousedown listeners from closing dropdown when clicking activator
          e.stopPropagation();
        }}
        onClick={() => {
          setShow((prev) => !prev);
        }}
      >
        {activator}
      </div>
      {usePortal && typeof document !== "undefined"
        ? createPortal(dropdownContent, document.body)
        : dropdownContent}
    </div>
  );
}
